<template>
  <div class="egg-template-container" :style="{ width: `${width}px`, height: `${height}px` }">
    <canvas
      ref="eggCanvas"
      class="w-full h-full"
      :width="canvasWidth"
      :height="canvasHeight"
    ></canvas>
  </div>
</template>

<script>
import { drawEggClipPath, getEggDimensions } from '@/utils/egg-shape';

export const EGG_TEMPLATES = [
  { id: 'white', name: 'Classic', baseColor: '#FFFFFF', accentColor: '#E8E0D4' },
  { id: 'pink', name: 'Rose', baseColor: '#FFE0EC', accentColor: '#FFB6D0' },
  { id: 'blue', name: 'Sky', baseColor: '#DBE9FF', accentColor: '#A8CBFF' },
  { id: 'green', name: 'Mint', baseColor: '#D4F5E0', accentColor: '#A0E8BA' },
  { id: 'yellow', name: 'Sunny', baseColor: '#FFF5D4', accentColor: '#FFE699' },
  { id: 'lavender', name: 'Lavender', baseColor: '#EDE0FF', accentColor: '#D4B8FF' },
];

export default {
  name: 'EggTemplate',
  props: {
    templateId: {
      type: String,
      default: 'white',
    },
    customColor: {
      type: String,
      default: null,
    },
    width: {
      type: Number,
      default: 300,
    },
    height: {
      type: Number,
      default: 375,
    },
  },
  emits: ['loaded'],
  data() {
    return {
      canvasWidth: this.width,
      canvasHeight: this.height,
    };
  },
  computed: {
    template() {
      return EGG_TEMPLATES.find(t => t.id === this.templateId) || EGG_TEMPLATES[0];
    },
  },
  watch: {
    templateId() {
      this.renderEgg();
    },
    customColor() {
      this.renderEgg();
    },
  },
  mounted() {
    const dpr = window.devicePixelRatio || 1;
    this.canvasWidth = this.width * dpr;
    this.canvasHeight = this.height * dpr;
    this.$nextTick(() => {
      this.renderEgg();
      this.$emit('loaded');
    });
  },
  methods: {
    renderEgg() {
      const canvas = this.$refs.eggCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const { cx, cy, rx, ry } = getEggDimensions(canvas.width, canvas.height);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw egg shape with gradient fill
      ctx.save();
      drawEggClipPath(ctx, cx, cy, rx, ry);
      ctx.clip();

      // Base gradient
      const gradient = ctx.createRadialGradient(
        cx - rx * 0.3,
        cy - ry * 0.3,
        0,
        cx,
        cy,
        Math.max(rx, ry),
      );
      if (this.customColor) {
        gradient.addColorStop(0, this.customColor);
        gradient.addColorStop(1, this.darkenColor(this.customColor, 0.15));
      } else {
        gradient.addColorStop(0, this.template.baseColor);
        gradient.addColorStop(1, this.template.accentColor);
      }
      ctx.fillStyle = gradient;
      ctx.fill();

      // Subtle highlight for 3D effect
      const highlight = ctx.createRadialGradient(
        cx - rx * 0.25,
        cy - ry * 0.35,
        0,
        cx - rx * 0.25,
        cy - ry * 0.35,
        rx * 0.6,
      );
      highlight.addColorStop(0, 'rgba(255,255,255,0.4)');
      highlight.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = highlight;
      drawEggClipPath(ctx, cx, cy, rx, ry);
      ctx.fill();

      ctx.restore();

      // Draw egg outline
      ctx.save();
      drawEggClipPath(ctx, cx, cy, rx, ry);
      ctx.strokeStyle = this.customColor
        ? this.darkenColor(this.customColor, 0.2)
        : this.template.accentColor;
      ctx.lineWidth = 2 * (window.devicePixelRatio || 1);
      ctx.stroke();
      ctx.restore();
    },

    darkenColor(hex, amount) {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
      const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
      const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    },

    getCanvas() {
      return this.$refs.eggCanvas;
    },
  },
};
</script>

<style scoped>
.egg-template-container {
  position: relative;
}
</style>
